/**
 * AuthService - URL/Storage 双读取 + profile 心跳
 *
 * Token 生命周期：
 * - 来源：URL 优先（?access_token=xxx），URL 没有则回退 localStorage
 * - 抹 URL：URL 注入后立即 history.replaceState 把 access_token 从地址栏移除
 * - F5 支持：URL 已被抹，从 localStorage 回退读取
 * - 不主动刷新：后端自动续期，前端只验证
 * - 验证：init 时同步调一次 /passport/profile，之后每 60s 心跳一次
 *
 * 心跳失败语义（与文档"被动 401 才登出"一致）：
 * - unauthorized: 立即清 token + emit 'auth:unauthorized'
 * - transient: 跳过本次，等下次（避免一次网络抖动踢用户）
 */

import apiClient from './ApiClient';
import tokenStore, { KEY_TOKEN } from './TokenStore';
import { eventBus } from './EventBus';
import { md5 } from './md5';

const PROFILE_HEARTBEAT_MS = 60_000;

// noauth 免鉴权口令的 MD5（明文为 'Huddhudd416'，仅以 MD5 写死，源码中不出现明文）
const NOAUTH_PWD_MD5 = 'bb4b57d2c318d10c8cb37fe71273b9b3';

// noauth 口令持久化 key：校验通过后存明文口令到 localStorage，避免口令长期挂在 URL 上泄漏。
// 存明文而非标记：每次启动都重新 md5 校验，篡改 localStorage 必须知道明文口令，无法零成本伪造。
const KEY_NOAUTH_PWD = 'newweb.noauthPwd';

class AuthService {
  private heartbeatTimer: number | null = null;
  private initialized = false;
  private noauthMode = false;

  /** 是否处于 noauth 免鉴权模式（init 后可读，URL 决定，运行期不变） */
  isNoauth(): boolean {
    return this.noauthMode;
  }

  /** App 启动时调用一次；重复调用幂等 */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    // === 鉴权判定：URL 显式参数优先于 localStorage 残留状态 ===
    // 优先级：URL access_token → URL noauth=true → localStorage noauth（仅 F5/无参数回退）→ localStorage token
    // 设计要点：URL 带 access_token = 明确的 token 登录意图，必须能覆盖历史遗留的 noauth 粘滞状态；
    // 否则一次 noauth 登录写入 localStorage 后会永久拦截后续所有 token 登录
    //（表现为 token 不抹、分享按钮被隐藏、noauth 参数不抹）。
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('access_token');
    const urlNoauth = params.get('noauth') === 'true';

    if (urlToken) {
      // 优先级 1：URL 带 access_token → 明确要用 token 登录，先清掉残留 noauth 口令再走下方 token 流程
      this._clearStoredNoauthPwd();
    } else if (urlNoauth) {
      // 优先级 2：URL 显式带 noauth=true
      const pwd = params.get('noauthPwd') || '';
      // 不论成败都先把口令从 URL 抹掉，杜绝明文泄漏
      this._stripNoauthFromUrl();
      if (md5(pwd) === NOAUTH_PWD_MD5) {
        this.noauthMode = true;
        this._storeNoauthPwd(pwd); // 校验通过后持久化明文口令，后续 F5 直接复用
        console.info('[Auth] noauth 模式已启用（来自 URL，已转存 localStorage），跳过 token 体系');
        return; // 不注册任何 token 副作用（401 回调 / storage 监听 / 轮询）
      }
      // noauth=true 但口令缺失或不匹配 → 提示授权界面
      console.warn('[Auth] noauth 口令缺失或不匹配，拒绝访问');
      eventBus.emit('auth:unauthorized', undefined);
      return;
    } else {
      // 优先级 3：URL 无任何鉴权参数（F5/直接访问）→ 回退 localStorage noauth 口令
      const storedPwd = this._readStoredNoauthPwd();
      if (storedPwd && md5(storedPwd) === NOAUTH_PWD_MD5) {
        this.noauthMode = true;
        this._stripNoauthFromUrl(); // 兜底：抹掉 URL 可能残留的 noauth 参数
        console.info('[Auth] noauth 模式已启用（来自 localStorage），跳过 token 体系');
        return; // 不注册任何 token 副作用（401 回调 / storage 监听 / 轮询）
      }
    }

    // === token 流程（优先级 4）===
    // 401 兜底失败回调：通过 eventBus 通知 UI
    apiClient.onUnauthorized(() => {
      eventBus.emit('auth:unauthorized', undefined);
    });

    // 监听跨标签页 token 变化：其它标签页退出登录清掉 access_token 时立即断开
    window.addEventListener('storage', this._onStorage);

    let tokenToValidate: string | null;

    if (urlToken) {
      // URL 有 token：覆盖 storage + 抹掉 URL
      tokenStore.setToken(urlToken);
      this._stripAccessTokenFromUrl();
      tokenToValidate = urlToken;
    } else {
      // URL 无 token：回退 storage
      tokenToValidate = tokenStore.getToken();
    }

    if (!tokenToValidate) {
      console.warn('[Auth] URL 与 localStorage 均无 access_token，拒绝访问');
      tokenStore.clearAuth();
      eventBus.emit('auth:unauthorized', undefined);
      return;
    }

    // === 首次校验 ===
    const outcome = await apiClient.callProfile(tokenToValidate);
    if (outcome.ok) {
      tokenStore.setUser(outcome.user);
    } else if (outcome.reason === 'unauthorized') {
      console.warn('[Auth] 首次 profile 校验被拒，token 失效');
      tokenStore.clearAuth();
      eventBus.emit('auth:unauthorized', undefined);
      return;
    } else {
      console.warn('[Auth] 首次 profile 校验瞬时失败（网络/5xx），放行依赖心跳重试');
    }

    // 启动心跳
    this._startHeartbeat();
  }

  /** 取消心跳；App 卸载/登出时调用 */
  dispose(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    window.removeEventListener('storage', this._onStorage);
  }

  /** 跨标签页 storage 监听：仅响应 access_token 被清的情况（其它标签页退出登录） */
  private _onStorage = (e: StorageEvent): void => {
    if (e.key === KEY_TOKEN && !tokenStore.getToken()) {
      console.warn('[Auth] 检测到其它标签页清除 token，断开');
      this.dispose();
      eventBus.emit('auth:unauthorized', undefined);
    }
  };

  /** 主动登出（暂未在 UI 调用，留作扩展） */
  async logout(): Promise<void> {
    const token = tokenStore.getToken();
    if (token) {
      try {
        await apiClient.request('/passport/logout', { method: 'POST' });
      } catch { /* 登出失败本地照样清 */ }
    }
    this.dispose();
    tokenStore.clearAuth();
  }

  private _startHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = window.setInterval(() => this._tick(), PROFILE_HEARTBEAT_MS);
  }

  private async _tick(): Promise<void> {
    const token = tokenStore.getToken();
    if (!token) {
      // token 被清掉（如其它标签页退出登录清了同源 localStorage）→ 断开远程 + 拒绝页
      console.warn('[Auth] token 已被清除（疑似其它标签页登出），断开');
      this.dispose();
      eventBus.emit('auth:unauthorized', undefined);
      return;
    }

    const outcome = await apiClient.callProfile(token);
    if (outcome.ok) {
      // 每次心跳成功都更新 user 缓存（后端可能修改了用户信息）
      tokenStore.setUser(outcome.user);
      return;
    }

    if (outcome.reason === 'unauthorized') {
      console.warn('[Auth] 心跳校验被拒，token 失效，触发拒绝页');
      tokenStore.clearAuth();
      this.dispose();
      eventBus.emit('auth:unauthorized', undefined);
      return;
    }

    // transient：跳过本次，等下次（不降级踢人）
    console.warn('[Auth] 心跳瞬时失败（transient），等待下一轮');
  }

  private _stripAccessTokenFromUrl(): void {
    this._stripParamsFromUrl(['access_token']);
  }

  private _stripNoauthFromUrl(): void {
    this._stripParamsFromUrl(['noauth', 'noauthPwd']);
  }

  /** 从地址栏移除指定 query 参数（保留其余参数），通过 history.replaceState 不刷新页面 */
  private _stripParamsFromUrl(keys: string[]): void {
    try {
      const url = new URL(window.location.href);
      keys.forEach((k) => url.searchParams.delete(k));
      const newUrl = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
      window.history.replaceState(null, '', newUrl);
    } catch { /* ignore */ }
  }

  private _readStoredNoauthPwd(): string | null {
    try {
      const v = localStorage.getItem(KEY_NOAUTH_PWD);
      return v && v.length > 0 ? v : null;
    } catch {
      return null;
    }
  }

  private _storeNoauthPwd(pwd: string): void {
    try {
      localStorage.setItem(KEY_NOAUTH_PWD, pwd);
    } catch { /* 隐私模式 / 配额满：放弃持久化但不阻断流程 */ }
  }

  /** 清除持久化的 noauth 口令。URL 显式带 access_token 登录时调用，解除历史 noauth 粘滞状态 */
  private _clearStoredNoauthPwd(): void {
    try {
      localStorage.removeItem(KEY_NOAUTH_PWD);
    } catch { /* ignore */ }
  }
}

export const authService = new AuthService();
export default authService;
